import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Clock, CheckCircle2, Building2, Hammer, 
  MapPin, Users, Zap, X, TrendingUp, Shield, 
  DollarSign, FileText, Phone, Home, Wrench, Timer,
  Briefcase, Target, ChevronRight
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { contactInfo } from '../data/mock';

const LendingPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Traditional vs Private comparison
  const comparisonData = [
    { feature: 'Approval Time', traditional: '30-60 days', private: '7-14 days', advantage: 'private' },
    { feature: 'Documentation', traditional: 'Extensive', private: 'Streamlined', advantage: 'private' },
    { feature: 'Credit Focus', traditional: 'Primary factor', private: 'Asset-based', advantage: 'private' },
    { feature: 'Flexibility', traditional: 'Rigid terms', private: 'Customizable', advantage: 'private' },
    { feature: 'Property Types', traditional: 'Limited', private: 'Diverse', advantage: 'private' }
  ];

  // Loan types
  const loanTypes = [
    {
      icon: Clock,
      title: 'Bridge Loans',
      description: 'Short-term financing to bridge the gap between acquisition and stabilization or permanent financing.',
      features: ['Quick closings', 'Flexible terms', '6-24 month terms', 'Interest-only options'],
      color: 'from-blue-500/20 to-blue-600/10',
      borderColor: 'border-blue-500/30',
      iconColor: 'text-blue-400'
    },
    {
      icon: Hammer,
      title: 'Fix & Flip',
      description: 'Purchase and renovation financing for investors executing value-add residential projects.',
      features: ['Purchase + rehab', 'Draw schedules', 'Fast approvals', 'Repeat borrower programs'],
      color: 'from-emerald-500/20 to-emerald-600/10',
      borderColor: 'border-emerald-500/30',
      iconColor: 'text-emerald-400'
    },
    {
      icon: Building2,
      title: 'Construction',
      description: 'Ground-up construction and major renovation financing for experienced developers.',
      features: ['New construction', 'Major rehab', 'Phased draws', 'Flexible structures'],
      color: 'from-amber-500/20 to-amber-600/10',
      borderColor: 'border-amber-500/30',
      iconColor: 'text-amber-400'
    }
  ];

  // Benefits
  const benefits = [
    { icon: Zap, title: 'Fast Approvals', description: 'Decisions in days, not months' },
    { icon: Target, title: 'Asset-Focused', description: 'Property value drives decisions' },
    { icon: FileText, title: 'Less Paperwork', description: 'Streamlined documentation' },
    { icon: Shield, title: 'Certainty of Close', description: 'Reliable execution' }
  ];

  // Target borrowers
  const targetBorrowers = [
    { icon: Home, title: 'Fix & Flip Investors', description: 'Residential rehab specialists' },
    { icon: Building2, title: 'Multifamily Operators', description: 'Apartment building investors' },
    { icon: Briefcase, title: 'Commercial Developers', description: 'Retail and office projects' },
    { icon: Wrench, title: 'Value-Add Operators', description: 'Renovation-focused investors' }
  ];

  // Service areas
  const serviceAreas = [
    { name: 'Houston Metro', markets: ['Inner Loop', 'The Woodlands', 'Sugar Land', 'Katy'] },
    { name: 'Greater Texas', markets: ['Dallas-Fort Worth', 'Austin', 'San Antonio'] },
    { name: 'Surrounding Areas', markets: ['Galveston', 'Pearland', 'Cypress', 'Conroe'] }
  ];

  return (
    <>
      <Helmet>
        <title>Private Real Estate Lending Services in Texas | ASAP Capital</title>
        <meta 
          name="description" 
          content="Private real estate lending solutions including bridge loans, fix and flip financing, and construction loans across Houston and Texas." 
        />
        <meta name="keywords" content="private real estate lending, bridge loans, fix and flip financing, construction loans, Houston private lenders, Texas real estate financing" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="/lending" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-[#D4AF37]/5 rounded-full blur-[180px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[150px] pointer-events-none" />
            
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-[0.03]">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, #D4AF37 1px, transparent 0)`,
                backgroundSize: '50px 50px'
              }} />
            </div>
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                {/* Left - Content */}
                <div>
                  {/* Pill badge */}
                  <motion.div 
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                    variants={itemVariants}
                  >
                    <DollarSign className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Private Lending</span>
                  </motion.div>

                  {/* H1 */}
                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                    variants={itemVariants}
                  >
                    Private Real Estate{' '}
                    <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                      Lending Solutions
                    </span>
                  </motion.h1>

                  {/* Subtitle */}
                  <motion.p 
                    className="text-base lg:text-lg text-white/60 leading-relaxed mb-8"
                    variants={itemVariants}
                  >
                    Fast, flexible financing for real estate investors when traditional 
                    banks can't deliver. Bridge loans, fix & flip, and construction 
                    financing across Houston and Texas.
                  </motion.p>

                  {/* Key stats */}
                  <motion.div 
                    className="flex flex-wrap gap-6 mb-10"
                    variants={itemVariants}
                  >
                    <div className="flex items-center gap-2">
                      <Timer className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-white/70 text-sm">7-14 Day Closings</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-white/70 text-sm">Asset-Based</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-white/70 text-sm">Texas Focus</span>
                    </div>
                  </motion.div>

                  {/* CTA buttons */}
                  <motion.div 
                    className="flex flex-wrap gap-4"
                    variants={itemVariants}
                  >
                    <Link to="/contact">
                      <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                        Discuss Your Financing
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </Link>
                    <a href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}>
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        Call Us
                      </Button>
                    </a>
                  </motion.div>
                </div>

                {/* Right - Visual card */}
                <motion.div 
                  variants={fadeInUp}
                  className="hidden lg:block"
                >
                  <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                    
                    <div className="relative z-10">
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 border border-[#D4AF37]/20">
                          <Zap className="h-8 w-8 text-[#D4AF37]" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Quick Quote</h3>
                        <p className="text-white/40 text-sm mt-1">Get started in minutes</p>
                      </div>
                      
                      <div className="space-y-3 mb-6">
                        {['Loan amount & property type', 'Timeline & exit strategy', 'Quick qualification review'].map((item, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-[#0A1628]/50 rounded-lg border border-white/5">
                            <div className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                              <span className="text-[#D4AF37] text-xs font-bold">{index + 1}</span>
                            </div>
                            <span className="text-white/60 text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                      
                      <Link to="/contact" className="block">
                        <button className="cta-contact w-full py-3 h-12 rounded-xl text-sm">
                          Start Conversation
                        </button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: BEYOND TRADITIONAL LENDERS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Why Private Lending
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Beyond Traditional Lenders
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    When time-sensitive opportunities require fast execution, private lending 
                    delivers where traditional banks cannot.
                  </motion.p>
                </div>

                {/* Comparison table */}
                <motion.div 
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl border border-white/10 overflow-hidden"
                  variants={itemVariants}
                >
                  {/* Header */}
                  <div className="grid grid-cols-3 bg-[#0A1628]/50 py-4 px-6 border-b border-white/10">
                    <div className="text-white/50 text-sm font-medium">Feature</div>
                    <div className="text-center">
                      <span className="text-white/40 text-sm">Traditional Banks</span>
                    </div>
                    <div className="text-center">
                      <span className="text-[#D4AF37] text-sm font-semibold">Private Lending</span>
                    </div>
                  </div>
                  
                  {/* Rows */}
                  {comparisonData.map((row, index) => (
                    <motion.div
                      key={row.feature}
                      className="grid grid-cols-3 py-4 px-6 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="text-white font-medium text-sm">{row.feature}</div>
                      <div className="text-center flex items-center justify-center gap-2">
                        <X className="h-4 w-4 text-red-400/70" />
                        <span className="text-white/40 text-sm">{row.traditional}</span>
                      </div>
                      <div className="text-center flex items-center justify-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        <span className="text-white text-sm">{row.private}</span>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: LOAN TYPES ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Financing Programs
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Loan Products
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Tailored financing solutions for every stage of your real estate investment.
                  </motion.p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {loanTypes.map((loan, index) => (
                    <motion.div
                      key={loan.title}
                      className={`bg-gradient-to-br ${loan.color} rounded-2xl p-6 border ${loan.borderColor} hover:border-opacity-60 transition-all duration-500 group relative overflow-hidden`}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 }}
                      whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}
                    >
                      {/* Background glow on hover */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${loan.color} opacity-0 group-hover:opacity-50 transition-opacity duration-500`} />
                      
                      <div className="relative z-10">
                        <div className={`w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-5 ${loan.borderColor} border`}>
                          <loan.icon className={`h-7 w-7 ${loan.iconColor}`} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">{loan.title}</h3>
                        <p className="text-white/50 text-sm mb-5">{loan.description}</p>
                        
                        <div className="space-y-2">
                          {loan.features.map((feature, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <CheckCircle2 className={`h-4 w-4 ${loan.iconColor} flex-shrink-0`} />
                              <span className="text-white/60 text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: FLEXIBLE TERMS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Our Advantage
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Flexible Terms
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      We understand that every deal is different. Our lending approach focuses on 
                      the property and your business plan, not just credit scores and tax returns. 
                      This allows us to structure financing that works for your specific situation.
                    </motion.p>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {benefits.map((benefit, index) => (
                        <motion.div
                          key={benefit.title}
                          className="flex items-start gap-3 p-4 bg-[#111D35]/50 rounded-xl border border-white/10"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                            <benefit.icon className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold text-sm">{benefit.title}</h4>
                            <p className="text-white/40 text-xs mt-1">{benefit.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Visual */}
                  <motion.div variants={fadeInUp}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <h4 className="text-white font-semibold mb-6">Typical Loan Parameters</h4>
                        
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">Loan Amounts</span>
                            <span className="text-white font-semibold">$100K - $5M+</span>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">Loan Terms</span>
                            <span className="text-white font-semibold">6 - 24 Months</span>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">LTV Range</span>
                            <span className="text-white font-semibold">Up to 75%</span>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">Closing Time</span>
                            <span className="text-emerald-400 font-semibold">7-14 Days</span>
                          </div>
                        </div>

                        <p className="text-white/30 text-xs mt-4 text-center">
                          Terms vary based on property, borrower experience, and deal structure
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: WHO WE SERVE ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Target Borrowers
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Who We Serve
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Our lending programs are designed for experienced real estate investors 
                    and operators who need reliable capital partners.
                  </motion.p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {targetBorrowers.map((borrower, index) => (
                    <motion.div
                      key={borrower.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 text-center group"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/20 transition-colors border border-[#D4AF37]/20">
                        <borrower.icon className="h-7 w-7 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-white font-semibold mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {borrower.title}
                      </h3>
                      <p className="text-white/50 text-sm">{borrower.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: SERVICE AREAS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Map visual placeholder */}
                  <motion.div variants={fadeInUp} className="order-2 lg:order-1">
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                          backgroundImage: `radial-gradient(circle at 30% 40%, #D4AF37 2px, transparent 0), radial-gradient(circle at 70% 30%, #D4AF37 2px, transparent 0), radial-gradient(circle at 50% 60%, #D4AF37 2px, transparent 0)`,
                          backgroundSize: '100% 100%'
                        }} />
                      </div>
                      
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-6">
                          <MapPin className="h-6 w-6 text-[#D4AF37]" />
                          <h4 className="text-white font-semibold text-lg">Texas Coverage</h4>
                        </div>
                        
                        <div className="space-y-4">
                          {serviceAreas.map((area, index) => (
                            <div key={area.name} className="p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                              <h5 className="text-white font-medium mb-2">{area.name}</h5>
                              <div className="flex flex-wrap gap-2">
                                {area.markets.map((market) => (
                                  <span 
                                    key={market}
                                    className="px-2 py-1 rounded bg-[#D4AF37]/10 text-[#D4AF37] text-xs"
                                  >
                                    {market}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Content */}
                  <div className="order-1 lg:order-2">
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Local Expertise
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Service Areas
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-6"
                      variants={itemVariants}
                    >
                      We focus primarily on the Houston metropolitan area and select Texas 
                      markets where our local expertise and relationships provide a competitive 
                      advantage for borrowers.
                    </motion.p>
                    
                    <motion.div className="space-y-3 mb-8" variants={itemVariants}>
                      {['Deep knowledge of Houston submarkets', 'Established contractor relationships', 'Local market intelligence', 'Quick property assessments'].map((point, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                          <span className="text-white/60">{point}</span>
                        </div>
                      ))}
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <Link to="/portfolio">
                        <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2">
                          View Our Portfolio
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== CTA SECTION ==================== */}
          <section className="py-24 lg:py-32 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                {/* Background accents */}
                <div className="absolute top-0 left-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 text-center">
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                    Get Started Today
                  </h2>
                  <p className="text-white/50 max-w-xl mx-auto mb-8">
                    Discuss your financing needs with our lending team. We'll help you determine 
                    the right solution for your investment opportunity.
                  </p>
                  
                  <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/contact">
                      <button className="cta-contact px-8 py-3 h-14 rounded-full text-base">
                        Discuss Your Financing
                        <ArrowRight className="h-5 w-5" />
                      </button>
                    </Link>
                    <a href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`}>
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-14 rounded-full text-base flex items-center gap-2">
                        <Phone className="h-5 w-5" />
                        {contactInfo.phone}
                      </Button>
                    </a>
                  </div>

                  {/* Disclaimer */}
                  <p className="text-white/30 text-xs mt-10 max-w-2xl mx-auto">
                    All financing subject to underwriting approval. Rates, terms, and loan 
                    amounts depend on property type, borrower qualifications, and market conditions. 
                    ASAP Capital reserves the right to modify or discontinue lending programs.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default LendingPage;
